import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

// ── Lightbox ──────────────────────────────────────────────────────────────────
const Lightbox = ({ photos, index, onClose }) => {
  const [current, setCurrent] = useState(index);
  const touchStartX = useRef(null);

  const goPrev = useCallback(() => setCurrent(i => (i > 0 ? i - 1 : photos.length - 1)), [photos.length]);
  const goNext = useCallback(() => setCurrent(i => (i < photos.length - 1 ? i + 1 : 0)), [photos.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft')  goPrev();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'Escape')     onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goPrev, goNext, onClose]);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd   = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) { delta < 0 ? goNext() : goPrev(); }
    touchStartX.current = null;
  };

  const photo = photos[current];
  if (!photo) return null;

  const handleShare = async () => {
    const photoUrl = `${window.location.origin}${API_URL}${photo.url}`;
    if (navigator.share) {
      try { await navigator.share({ title: photo.file_name, url: photoUrl }); } catch {}
    } else {
      await navigator.clipboard.writeText(photoUrl);
      alert('Photo link copied to clipboard!');
    }
  };

  return (
    <div style={lb.overlay} onClick={onClose} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div style={lb.box} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={lb.header}>
          <span style={lb.counter}>{current + 1} / {photos.length}</span>
          <button style={lb.closeBtn} onClick={onClose}>✕ Close</button>
        </div>

        {/* Photo */}
        <div style={lb.imgWrap}>
          <img src={`${API_URL}${photo.url}`} alt={photo.file_name} style={lb.img} />
          {/* Share — bottom-right of photo */}
          <button style={lb.shareBtn} onClick={handleShare} title="Share photo">
            Share
          </button>
        </div>

        {/* Nav + download below photo */}
        <div style={lb.controls}>
          {photos.length > 1 && (
            <button style={lb.navBtn} onClick={goPrev}>&#8249; Prev</button>
          )}
          <a
            href={`${API_URL}${photo.url}`}
            download={photo.file_name}
            style={lb.downloadBtn}
          >
            ⬇ Download
          </a>
          {photos.length > 1 && (
            <button style={lb.navBtn} onClick={goNext}>Next &#8250;</button>
          )}
        </div>
      </div>
    </div>
  );
};

const lb = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.94)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0' },
  box: { width: '100%', maxWidth: 860, display: 'flex', flexDirection: 'column', maxHeight: '100vh', padding: '0.75rem' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' },
  counter: { color: '#a78bfa', fontWeight: 700, fontSize: '0.95rem' },
  closeBtn: { background: 'rgba(255,255,255,0.12)', color: '#fff', border: 'none', borderRadius: '0.4rem', padding: '0.4rem 0.9rem', cursor: 'pointer', fontWeight: 600 },
  imgWrap: { position: 'relative', flex: 1, overflow: 'hidden', borderRadius: '0.75rem', background: '#111' },
  img: { width: '100%', maxHeight: 'calc(100vh - 140px)', objectFit: 'contain', display: 'block', borderRadius: '0.75rem' },
  shareBtn: { position: 'absolute', bottom: '0.75rem', right: '0.75rem', background: 'rgba(0,0,0,0.65)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '0.5rem', padding: '0.45rem 0.9rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', backdropFilter: 'blur(6px)' },
  controls: { display: 'flex', gap: '0.75rem', justifyContent: 'center', alignItems: 'center', marginTop: '0.75rem', flexWrap: 'wrap' },
  navBtn: { background: 'rgba(255,255,255,0.12)', color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 1.25rem', cursor: 'pointer', fontWeight: 700, fontSize: '0.95rem' },
  downloadBtn: { background: '#a78bfa', color: '#fff', borderRadius: '0.5rem', padding: '0.5rem 1.25rem', textDecoration: 'none', fontWeight: 700, fontSize: '0.9rem' },
};

// ── Photo grid ────────────────────────────────────────────────────────────────
const PhotoGrid = ({ eventId }) => {
  const [photos, setPhotos]   = useState([]);
  const [page,   setPage]     = useState(1);
  const [pages,  setPages]    = useState(1);
  const [total,  setTotal]    = useState(0);
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/events/${eventId}/photo-browser?page=${p}&limit=60`);
      setPhotos(prev => p === 1 ? res.data.photos : [...prev, ...res.data.photos]);
      setPage(p);
      setPages(res.data.pages);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  }, [eventId]);

  useEffect(() => { load(1); }, [load]);

  const allPhotos = photos;

  return (
    <div>
      <p style={s.subCount}>{total} photos total</p>
      <div style={s.grid}>
        {photos.map((photo, idx) => (
          <div key={photo.id} style={s.gridCard} onClick={() => setLightbox(idx)}>
            <img src={`${API_URL}${photo.url}`} alt={photo.file_name} style={s.gridImg} loading="lazy" />
          </div>
        ))}
      </div>
      {page < pages && (
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <button style={s.loadMoreBtn} onClick={() => load(page + 1)} disabled={loading}>
            {loading ? 'Loading…' : `Load more (${total - photos.length} remaining)`}
          </button>
        </div>
      )}
      {lightbox !== null && (
        <Lightbox photos={allPhotos} index={lightbox} onClose={() => setLightbox(null)} />
      )}
    </div>
  );
};

// ── Visitor accordion ─────────────────────────────────────────────────────────
const VisitorRow = ({ visitor, eventId, searchTerm }) => {
  const [open,     setOpen]     = useState(false);
  const [data,     setData]     = useState(null); // { photos, total, has_more }
  const [loading,  setLoading]  = useState(false);
  const [lightbox, setLightbox] = useState(null);

  if (searchTerm) {
    const q = searchTerm.toLowerCase();
    if (!visitor.full_name.toLowerCase().includes(q) && !visitor.mobile_number.includes(q)) return null;
  }

  const expand = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (data !== null) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/events/${eventId}/visitor-matches/${visitor.id}/photos`);
      setData(res.data);
    } catch { setData({ photos: [], total: 0, has_more: false }); }
    finally { setLoading(false); }
  };

  const photos = data?.photos ?? [];

  return (
    <div style={s.visitorCard}>
      <div style={s.visitorRow} onClick={expand}>
        <div style={s.visitorLeft}>
          {visitor.selfie_url
            ? <img src={`${API_URL}${visitor.selfie_url}`} alt="" style={s.selfieThumb} />
            : <div style={s.selfieEmpty}>👤</div>}
          <div>
            <p style={s.visitorName}>{visitor.full_name}</p>
            <p style={s.visitorMeta}>{visitor.mobile_number}</p>
          </div>
        </div>
        <div style={s.visitorRight}>
          <span style={s.matchBadge}>{visitor.match_count} photos</span>
          {visitor.gallery_url && (
            <a href={visitor.gallery_url} target="_blank" rel="noreferrer" style={s.galleryLink}
               onClick={e => e.stopPropagation()}>
              Gallery ↗
            </a>
          )}
          <span style={s.chevron}>{open ? '▲' : '▼'}</span>
        </div>
      </div>

      {open && (
        <div style={s.visitorPhotos}>
          {loading && <p style={s.loadingText}>Loading…</p>}
          {!loading && photos.length === 0 && <p style={s.loadingText}>No matched photos for this visitor.</p>}
          {photos.length > 0 && (
            <>
              <div style={{ ...s.grid, gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '0.5rem' }}>
                {photos.map((photo, idx) => (
                  <div key={photo.id} style={s.gridCard} onClick={() => setLightbox(idx)}>
                    <img src={`${API_URL}${photo.url}`} alt={photo.file_name} style={s.gridImg} loading="lazy" />
                  </div>
                ))}
              </div>
              {data.has_more && (
                <a href={visitor.gallery_url} target="_blank" rel="noreferrer" style={s.moreLink}>
                  +{data.total - 10} more photos — open full gallery ↗
                </a>
              )}
            </>
          )}
          {lightbox !== null && (
            <Lightbox photos={photos} index={lightbox} onClose={() => setLightbox(null)} />
          )}
        </div>
      )}
    </div>
  );
};

const VisitorList = ({ eventId }) => {
  const [visitors,    setVisitors]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [searchTerm,  setSearchTerm]  = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/events/${eventId}/visitor-matches`)
      .then(res => setVisitors(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [eventId]);

  if (loading) return <p style={s.loadingText}>Loading visitors…</p>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <p style={s.subCount}>{visitors.length} registered visitors</p>
        <input
          style={s.searchInput}
          placeholder="Search by name or mobile…"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      </div>
      {visitors.map(v => (
        <VisitorRow key={v.id} visitor={v} eventId={eventId} searchTerm={searchTerm} />
      ))}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const EventPhotosViewer = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('photos');
  const [eventName, setEventName] = useState('');

  const backUrl = searchParams.get('from') === 'super'
    ? `/admin/super/events/${id}`
    : `/admin/events/${id}`;

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/events/${id}`)
      .then(res => setEventName(res.data.name))
      .catch(() => {});
  }, [id]);

  return (
    <div style={s.page}>
      <div style={{ padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid #e5e7eb', background: '#fff' }}>
        <button style={s.backBtn} onClick={() => navigate(backUrl)}>← Back</button>
        <span style={{ fontWeight: 700, color: '#1a1a2e' }}>{eventName || 'Event'} — Photo Browser</span>
      </div>

      {/* Sticky tabs */}
      <div style={s.tabBar}>
        <button style={{ ...s.tab, ...(tab === 'photos'   ? s.tabActive : {}) }} onClick={() => setTab('photos')}>
          All Photos
        </button>
        <button style={{ ...s.tab, ...(tab === 'visitors' ? s.tabActive : {}) }} onClick={() => setTab('visitors')}>
          Visitor Matches
        </button>
      </div>

      <div style={s.content}>
        {tab === 'photos'   && <PhotoGrid   eventId={id} />}
        {tab === 'visitors' && <VisitorList eventId={id} />}
      </div>
    </div>
  );
};

const s = {
  page: { minHeight: '100vh', background: '#f8fafc', fontFamily: "'Segoe UI', system-ui, sans-serif" },
  nav: { display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 1.5rem', background: '#fff', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 10 },
  backBtn: { background: '#f3f4f6', border: 'none', borderRadius: '0.5rem', padding: '0.4rem 0.9rem', cursor: 'pointer', fontWeight: 600, color: '#374151' },
  navTitle: { fontWeight: 700, fontSize: '1rem', color: '#1a1a2e' },
  tabBar: { display: 'flex', background: '#fff', borderBottom: '2px solid #e5e7eb', position: 'sticky', top: '52px', zIndex: 9 },
  tab: { flex: 1, padding: '0.875rem', border: 'none', background: 'none', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer', color: '#6b7280', borderBottom: '3px solid transparent', marginBottom: '-2px' },
  tabActive: { color: '#4f46e5', borderBottom: '3px solid #4f46e5' },
  content: { padding: '1.5rem', maxWidth: 1100, margin: '0 auto' },
  subCount: { margin: '0 0 1rem', color: '#6b7280', fontSize: '0.875rem' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.75rem' },
  gridCard: { borderRadius: '0.75rem', overflow: 'hidden', aspectRatio: '1', background: '#e5e7eb', cursor: 'pointer' },
  gridImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'opacity 0.2s' },
  loadMoreBtn: { padding: '0.6rem 1.5rem', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '0.5rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' },
  loadingText: { color: '#9ca3af', textAlign: 'center', padding: '2rem 0', margin: 0 },
  // Visitor accordion
  visitorCard: { background: '#fff', borderRadius: '0.75rem', marginBottom: '0.6rem', overflow: 'hidden', border: '1px solid #e5e7eb' },
  visitorRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.875rem 1rem', cursor: 'pointer', gap: '1rem' },
  visitorLeft: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  selfieThumb: { width: 42, height: 42, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '2px solid #e0e7ff' },
  selfieEmpty: { width: 42, height: 42, borderRadius: '50%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', flexShrink: 0 },
  visitorName: { margin: 0, fontWeight: 700, color: '#1a1a2e', fontSize: '0.9rem' },
  visitorMeta: { margin: '0.1rem 0 0', color: '#6b7280', fontSize: '0.8rem' },
  visitorRight: { display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 },
  matchBadge: { background: '#ede9fe', color: '#6d28d9', borderRadius: '50px', padding: '0.2rem 0.65rem', fontSize: '0.78rem', fontWeight: 700 },
  galleryLink: { color: '#4f46e5', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none' },
  chevron: { color: '#9ca3af', fontSize: '0.75rem' },
  visitorPhotos: { borderTop: '1px solid #f3f4f6', padding: '0.75rem 1rem 1rem' },
  searchInput: { padding: '0.45rem 0.9rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '0.875rem', outline: 'none', minWidth: 220 },
  moreLink: { display: 'inline-block', marginTop: '0.6rem', color: '#4f46e5', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none' },
};

export default EventPhotosViewer;
